/**
 * iter132 — Push notification opt-in banner (customer Dashboard).
 *
 * Nudges customers to enable notifications so they get invoice + payment-due
 * alerts + on-the-way / arrived / completed pings from the crew in real
 * time. Only shown when:
 *   - The browser supports web push
 *   - Notification permission has NOT already been granted
 *   - Notification permission has NOT been outright denied (nothing we can
 *     do from the app if they said no)
 *   - The user hasn't dismissed the banner this session
 */
import { useState } from "react";
import { Button } from "./ui/button";
import { Bell, X, Loader2 } from "lucide-react";
import { usePushNotifications } from "../lib/pwa";
import { toast } from "sonner";

export default function PushOptInBanner() {
  const { supported, permission, subscribed, busy, subscribe } = usePushNotifications();
  const [dismissed, setDismissed] = useState(
    () => typeof window !== "undefined" && sessionStorage.getItem("pushOptInDismissed") === "1"
  );

  // Don't show if unsupported, already subscribed, blocked, or dismissed.
  if (!supported || subscribed || permission === "denied" || dismissed) return null;

  const dismiss = () => {
    sessionStorage.setItem("pushOptInDismissed", "1");
    setDismissed(true);
  };

  const handleEnable = async () => {
    const r = await subscribe();
    if (r.ok) {
      toast.success("Notifications enabled — you'll now get real-time updates from JDSN.");
    } else if (r.reason === "denied") {
      toast.error("You blocked notifications. Enable them from your browser's site settings if you change your mind.");
    } else {
      toast.error(`Could not enable notifications: ${r.reason || "unknown error"}`);
    }
  };

  return (
    <div
      className="mt-4 rounded-2xl bg-gradient-to-r from-[#047857] to-[#065F46] p-4 shadow-lg shadow-emerald-900/20 flex items-center gap-3"
      data-testid="push-optin-banner"
    >
      <div className="grid place-items-center w-11 h-11 rounded-full bg-white/15 backdrop-blur">
        <Bell className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-white font-bold text-sm sm:text-base leading-tight">
          Turn on notifications
        </div>
        <div className="text-white/85 text-xs sm:text-sm">
          Get real-time updates for invoices, when we&apos;re on the way, and when your service is complete.
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Button
          size="sm"
          onClick={handleEnable}
          disabled={busy}
          className="rounded-full bg-white text-[#047857] hover:bg-white/90 font-bold px-4 shadow-md"
          data-testid="push-optin-enable-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Enable"}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={dismiss}
          className="rounded-full h-9 w-9 p-0 text-white/85 hover:bg-white/10 hover:text-white"
          data-testid="push-optin-dismiss-btn"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
